
import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { isEmpty } from 'lodash';
import { Moment } from 'moment';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Site, InvestigatorSitesStorage } from '../../../../sites';
import { ProjectsStorage, Project } from './../../../shared';

export interface SitesData {
    allSites: boolean | null;
    selectedSites?: Site[];
    siteLimitDate?: Moment;
}

@Component({
    selector: 'sites-select',
    template: require('./sites-select.component.html'),
    styles: [require('./sites-select.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SitesSelectComponent implements OnInit, OnDestroy {
    @Input()
    public sitesFormGroup: FormGroup;

    @Input()
    public showLimitDate = true;

    @Input()
    public showAllSites = true;

    @Input()
    public multiSelectProject = false;

    @Input()
    public set projects(projects: Project[]) {
        this.project$.next(projects);

        this.selectedSitesControl.setValue([]);

        this.disabled = isEmpty(projects);
        
        if (this.disabled) {
            this.allSitesControl.disable();
        } else {
            this.allSitesControl.enable();
        }
    }

    @Output()
    public valueChanged = new EventEmitter<SitesData>();

    @Input()
    @HostBinding('class.disabled')
    public disabled = false;

    public readonly selectedSitesControl = new FormControl(null, Validators.required);
    public readonly allSitesControl = new FormControl(false);
    public readonly siteLimitDateControl = new FormControl();

    public sites$: Observable<Site[]>;
    private project$ = new BehaviorSubject<Project[]>([]);
    private subscription = new Subscription();

    constructor(private projectStorage: ProjectsStorage, private siteStorage: InvestigatorSitesStorage) { }

    public ngOnInit() {
        this.sitesFormGroup.addControl('allSites', this.allSitesControl);
        this.sitesFormGroup.addControl('selectedSites', this.selectedSitesControl);

        this.subscription.add(
            this.allSitesControl.valueChanges.subscribe(isAllSites => {
                if (isAllSites) {
                    this.selectedSitesControl.setValue([]);
                    this.sitesFormGroup.removeControl('selectedSites');
                    this.sitesFormGroup.addControl('siteLimitDate', this.siteLimitDateControl);
                } else {
                    this.siteLimitDateControl.setValue(null);
                    this.sitesFormGroup.removeControl('siteLimitDate');
                    this.sitesFormGroup.addControl('selectedSites', this.selectedSitesControl);
                }
            })
        );

        this.subscription.add(
            this.sitesFormGroup.valueChanges.subscribe(() => {
                this.valueChanged.emit({
                    allSites: this.allSitesControl.value,
                    selectedSites: this.selectedSitesControl.value || [],
                    siteLimitDate: this.siteLimitDateControl.value
                });
            })
        );

        if (this.multiSelectProject) {
            this.sites$ = this.project$.pipe(switchMap(projects =>
                isEmpty(projects)
                    ? []
                    : this.siteStorage.getMultiSelectedProjectSites(projects))
            );

        } else {
            this.sites$ = this.project$.pipe(switchMap(projects =>
                isEmpty(projects)
                    ? []
                    : this.projectStorage.getProjectSites(projects[0].subProtocolId))
            );
        }  
    }

    public ngOnDestroy() {
        this.project$.complete();
        this.subscription.unsubscribe();
    }

    public onSitesChange(selectedSites: Site[]) {
        this.selectedSitesControl.setValue(selectedSites);
    }
}
